import { useId, useMemo } from 'react';
import { Button, Input, Modal } from '@ai-tutor/ui';
import { ChevronRight, Loader2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { useScopeSource } from '@/hooks/lesson/useSources';
import { unitCount, unitNoun } from '@/lib/sources/scopeSelection';
import type { SourceItem } from '@/lib/api-client';
import { ScopePartRow } from './scope/ScopePartRow';
import { SheetRowsInput } from './scope/SheetRowsInput';
import { ScopeSummary } from './scope/ScopeSummary';
import { useScopePicker, type ScopePicker } from './scope/useScopePicker';

interface OutlineScopingDialogProps {
  lessonId: string;
  source: SourceItem; // an AWAITING_SCOPE source
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The source picker, for every kind of file. A source waits here when it is
 * over its per-file limit or would go past the month's pages: the student
 * picks parts (chapters, slide groups, sheets) or types the pages/slides to
 * use, and only that is parsed. Parts start pre-selected to fit; nothing is
 * cut without the student choosing.
 *
 * Front/back matter (contents, index, ...) is flagged server-side (`is_matter`)
 * and tucked into a collapsed group, deselected by default.
 */
export function OutlineScopingDialog({
  lessonId,
  source,
  isOpen,
  onClose,
}: OutlineScopingDialogProps) {
  const picker = useScopePicker(source);
  const scope = useScopeSource(lessonId, source.source_id);

  const submit = async () => {
    if (!picker.canSubmit) return;
    try {
      await scope.mutateAsync(picker.submission());
      notify.success('Building your lesson from your selection…');
      onClose();
    } catch (err) {
      notify.error(err, "Couldn't apply your selection");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Which parts should we use?'
      size='lg'
      className='h-[600px]'
    >
      <div className='flex h-full flex-col'>
        <PickerReason source={source} picker={picker} />
        <div className='min-h-0 flex-1 overflow-y-auto px-4 pb-2'>
          <PartsList picker={picker} disabled={picker.usingRanges} />
          {picker.unit !== 'cell' && <RangeInput picker={picker} />}
        </div>
        <div className='flex items-center justify-between gap-4 border-t border-border px-6 py-4 max-sm:flex-col max-sm:items-stretch max-sm:gap-3 max-sm:px-4'>
          <ScopeSummary picker={picker} />
          <div className='flex items-center gap-2 max-sm:[&>button]:h-11 max-sm:[&>button]:flex-1'>
            <Button variant='ghost' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!picker.canSubmit || scope.isPending}
            >
              {scope.isPending && (
                <Loader2 className='h-4 w-4 animate-spin motion-reduce:animate-none' />
              )}
              Build lesson
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** Why the picker opened, in the source's own unit. */
function PickerReason({
  source,
  picker,
}: {
  source: SourceItem;
  picker: ScopePicker;
}) {
  const { unit, total, limit, pagesLeft } = picker;
  const size =
    total !== null
      ? `${source.size_estimated ? 'about ' : ''}${unitCount(unit, total)}`
      : null;
  const why =
    source.scope_reason === 'over_monthly_allowance'
      ? `You have ${(pagesLeft ?? 0).toLocaleString()} pages left this month, so choose what to use.`
      : limit !== null
        ? `Your plan reads up to ${unitCount(unit, limit)} per file, so choose what to use.`
        : 'Choose what to use.';
  return (
    <p className='px-6 pb-1 pt-4 text-[13px] text-muted-foreground'>
      <span className='font-medium text-foreground'>{source.name}</span>
      {size && <> has {size}. </>}
      {!size && '. '}
      {why} The rest stays listed, and you can add it to a new version later.
    </p>
  );
}

function PartsList({
  picker,
  disabled,
}: {
  picker: ScopePicker;
  disabled: boolean;
}) {
  const { sections, unit, selected, toggle, setAll } = picker;
  const parts = useMemo(() => sections.filter(s => !s.is_matter), [sections]);
  const matter = useMemo(() => sections.filter(s => s.is_matter), [sections]);
  const maxUnits = Math.max(1, ...parts.map(s => s.est_units));
  if (sections.length === 0) return null;

  const chosen = parts.filter(s => selected.has(s.id)).length;
  const all = parts.length > 0 && chosen === parts.length;
  const row = (s: (typeof sections)[number]) => (
    <div key={s.id}>
      <ScopePartRow
        section={s}
        unit={unit}
        selected={!disabled && selected.has(s.id)}
        maxUnits={maxUnits}
        disabled={disabled}
        onToggle={toggle}
      />
      {unit === 'cell' && selected.has(s.id) && (s.rows ?? 0) > 1 && (
        <SheetRowsInput
          section={s}
          value={picker.rowText[s.id] ?? ''}
          onChange={text => picker.setRows(s.id, text)}
        />
      )}
    </div>
  );

  return (
    <>
      <div className='flex items-center justify-between px-2 py-3'>
        <button
          type='button'
          onClick={() =>
            setAll(
              parts.map(s => s.id),
              !all
            )
          }
          disabled={disabled || parts.length === 0}
          className='tap-target text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40'
        >
          {all ? 'Clear all' : 'Select all'}
        </button>
        <span className='text-[13px] text-muted-foreground'>
          {chosen} of {parts.length} {unit === 'cell' ? 'sheets' : 'parts'}
        </span>
      </div>
      {parts.map(row)}
      {matter.length > 0 && (
        <details className='group mt-1'>
          <summary className='flex cursor-pointer list-none items-center gap-2 rounded-md px-3 py-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-accent/70 [&::-webkit-details-marker]:hidden'>
            <ChevronRight className='h-3.5 w-3.5 transition-transform group-open:rotate-90' />
            <span className='font-medium'>Front &amp; back matter</span>
            <span className='truncate'>
              · {matter.map(s => s.title).join(', ')}
            </span>
          </summary>
          <div className='pt-0.5'>{matter.map(row)}</div>
        </details>
      )}
    </>
  );
}

/** Type pages or slides directly ("1-30, 45-60"); overrides the parts. */
function RangeInput({ picker }: { picker: ScopePicker }) {
  const noun = unitNoun(picker.unit);
  // Unique per picker: a fixed id let a label point at another dialog's box.
  const id = useId();
  return (
    <div className='mt-3 px-2 pb-2'>
      <label htmlFor={id} className='text-[13px] font-medium text-foreground'>
        {picker.sections.length > 0
          ? `Or type the ${noun} to use`
          : `Type the ${noun} to use`}
      </label>
      <Input
        id={id}
        value={picker.rangeText}
        onChange={e => picker.typeRanges(e.target.value)}
        placeholder={
          picker.total
            ? `e.g. 1-30, 45-60 (of ${picker.total})`
            : 'e.g. 1-30, 45-60'
        }
        inputMode='numeric'
        aria-invalid={Boolean(picker.rangeError)}
        aria-describedby={`${id}-hint`}
        className='mt-1.5'
      />
      <p id={`${id}-hint`} className='mt-1 text-xs text-muted-foreground'>
        {picker.rangeError ? (
          <span className='text-destructive'>{picker.rangeError}</span>
        ) : picker.usingRanges ? (
          `Using ${unitCount(picker.unit, picker.selectedUnits)}; clear this box to pick parts instead.`
        ) : (
          'Separate ranges with commas.'
        )}
      </p>
    </div>
  );
}
